import { showModalForm } from '@components/modal-form';
import { StandardDialog } from '@components/dialog';
import { useState, useMemo } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { DEFAULT_CONTENT } from '@components/messages';
import { useRouter } from '@components/router';
import { IcoSearch, IcoUsers } from '@components/icons';
import dayjs from 'dayjs';

type Course = RpxResponse<typeof rpx.messages.getComposableCourses>[number];

type Props = {
  courses: Course[];
};

export function showCoursePickerModal(props: Props) {
  showModalForm(({ resolve }) => <CoursePicker courses={props.courses} resolve={resolve} />);
}

function CoursePicker({ courses, resolve }: Props & { resolve(): void }) {
  const router = useRouter();
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return courses;
    }
    return courses.filter((c) => {
      return (
        c.title.toLowerCase().includes(q) ||
        c.guideName.toLowerCase().includes(q) ||
        c.guideEmail.toLowerCase().includes(q)
      );
    });
  }, [courses, search]);

  async function pick(course: Course) {
    if (busy) {
      return;
    }
    let mimicked = false;
    try {
      setBusy(true);
      if (course.relationship === 'mimic') {
        await rpx.admin.mimicUser({ userId: course.guideId });
        mimicked = true;
      }
      const result = await rpx.messages.saveMessage({
        title: '',
        content: DEFAULT_CONTENT,
        courseId: course.id,
      });
      const returnParam = course.relationship === 'mimic' ? '?return=email-schedule' : '';
      resolve();
      router.goto(`/manage/courses/${course.id}/messages/${result.id}${returnParam}`);
    } catch (err) {
      // If the saveMessage step failed after we already mimicked, roll back so
      // the user isn't silently left operating as someone else.
      if (mimicked) {
        try {
          await rpx.admin.unmimicUser();
        } catch {
          // unmimic is idempotent server-side; ignore secondary failures.
        }
      }
      setBusy(false);
      showError(err);
    }
  }

  return (
    <StandardDialog onClose={resolve} title="Compose new message">
      <div class="flex flex-col gap-3 min-w-[28rem] max-w-2xl">
        <p class="text-sm text-gray-600">
          Pick the course this message belongs to. We'll create a draft and open the editor.
        </p>
        <div class="relative">
          <IcoSearch class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            autofocus
            placeholder="Search by course or guide…"
            class="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-indigo-500"
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          />
        </div>
        {filtered.length === 0 && (
          <div class="text-sm text-gray-500 py-8 text-center">No courses match.</div>
        )}
        {filtered.length > 0 && (
          <ul class="max-h-96 overflow-y-auto -mx-2 flex flex-col">
            {filtered.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => pick(c)}
                  class="w-full text-left px-3 py-3 rounded-lg hover:bg-indigo-50 disabled:opacity-50 flex flex-col gap-1"
                >
                  <span class="text-sm font-semibold text-gray-900">{c.title}</span>
                  <span class="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                    <span>by {c.guideName}</span>
                    <span class="inline-flex items-center gap-1">
                      <IcoUsers class="w-3 h-3" />
                      {c.studentCount.toLocaleString()}
                    </span>
                    {c.latestActivity && (
                      <span>last activity {dayjs(c.latestActivity).format('MMM D')}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </StandardDialog>
  );
}
